import { ApplicationRef } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { PopupService } from "src/app/services/popup/popup.service";
import {
  dxFormRunAsyncOperation,
  getDxCustomLabelFormItem,
  getDxFormItemPatientId
} from "src/app/support/dx";
import { sntTranslate } from "src/app/support/translate";
import DevExpress from "devextreme";
import { Pet } from "src/app/backend/models/pet/pet";
import { getCustomerNominative } from "src/app/support/customer";
import { dateUtil } from "src/app/support/date-util";
import { compact, first, keys, toPairs } from "lodash";
import { Customer } from "src/app/backend/models/customer/customer";
import { ReminderBackend } from "src/app/backend/api/reminder.backend";
import {
  Reminder,
  ReminderCreate,
  ReminderTemplate,
  ReminderTransport,
  ReminderType
} from "src/app/backend/models/reminder";
import { map } from "rxjs/operators";
import { initWysiwygEditor } from "src/app/support/tiny";
import { DxFormComponent } from "devextreme-angular";
import { ValueChangedEvent } from "devextreme/ui/select_box";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import { ReminderTemplateBackend } from "src/app/backend/api/reminder-template.backend";
import { Editor } from "tinymce";
import {
  DocumentDataModel,
  DocumentService
} from "src/app/services/document.service";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { reminderSendWhatsApp } from "src/app/modules/bootstrap/pms-ui/functions/reminder-send-whatsapp.fucntion";
import { ToastService } from "src/app/services/toast.service";
import { Tenant } from "src/app/backend/models/tenant/tenant";
import { environment } from "src/environments/environment";
import { Member } from "src/app/backend/models/member/member";
import ArrayStore from "devextreme/data/array_store";
import { lastValueFrom } from "rxjs";
import dayjs from "dayjs";
import { MessageTemplateType } from "src/app/backend/models/message-template";
import { getPublicFileSrcById } from "src/app/support/public-file";

const CUSTOM_EXPIRE_DATE = "-1";

type CreateReminderFunctionProviders = {
  reminedBackend: ReminderBackend;
  popupService: PopupService;
  customerBackend: CustomerBackend;
  patientBackend: PetBackend;
  applicationRef: ApplicationRef;
  reminderTemplateBackend: ReminderTemplateBackend;
  documentService: DocumentService;
  bootstrapService: BootstrapService;
  toastService: ToastService;
};

type PopupData = {
  postingDate: string;
  expireDate: string | "-1" | undefined;
  expireDateCustom: string | undefined;
  notificationDate: string | undefined;
  patientId?: string;
  patientName?: string;
  customerId?: string;
  subject?: string;
  body?: string;
  channel: ReminderTransport;
  type: ReminderType;
  templateId?: string;
  recurring?: boolean;
  absoluteDate?: boolean;
  resources?: string[];
};

type CreateTemplateReminderFunctionInput = {
  type?: "vaccination";
};

type CreateReminderFunctionInput = CreateTemplateReminderFunctionInput & {
  patient: Pet | undefined;
  customer?: Customer | undefined;
};

type CreateTemplateReminderModalConfig = {
  dialog?: boolean;
  title?: string;
};

export async function createTemplateReminderFunction(
  input: CreateTemplateReminderFunctionInput,
  providers: CreateReminderFunctionProviders,
  modalConfig?: CreateTemplateReminderModalConfig,
  overrides?: {
    reminderCreateReady?: (
      reminderCreate: ReminderCreate & ReminderTemplate
    ) => void;
  }
): Promise<void> {
  return await createReminderFunction(
    {
      patient: undefined,
      type: input?.type
    },
    providers,
    undefined,
    {
      mode: "template",
      disableClone: true,
      disableWhatsApp: true
    },
    modalConfig,
    overrides
  );
}

export function createReminderFunction(
  data: CreateReminderFunctionInput,
  providers: CreateReminderFunctionProviders,
  cloneData?: PopupData,
  config?: {
    mode?: "template";
    disableClone?: boolean;
    disableWhatsApp?: boolean;
  },
  modalConfig?: CreateTemplateReminderModalConfig,
  overrides?: {
    reminderCreateReady?: (
      reminderCreate: ReminderCreate & ReminderTemplate
    ) => void;
  }
): Promise<void> {
  return new Promise(async (res, rej) => {
    let initialFormData: Partial<PopupData> &
      Record<string, string | boolean | number | undefined | string[]> = {
      patientId: data.patient?.id,
      patientName: data.patient?.name,
      postingDate: dateUtil().format("DD/MM/YYYY"),
      frequency: -1,
      resources: <string[]>[]
    };

    const customersFormItems: DevExpress.ui.dxForm.Item[] = data.patient
      ? data.patient.relatives?.map(
          (relative) =>
            <DevExpress.ui.dxForm.Item>{
              dataField: `customer-${relative.id}`,
              editorType: "dxCheckBox",
              editorOptions: {
                text: getCustomerNominative(relative),
                readOnly: data.patient!.relatives?.length === 1
              },
              label: {
                visible: false
              }
            }
        ) || []
      : data.customer
        ? [
            <DevExpress.ui.dxForm.Item>{
              dataField: `customer-${data.customer.id}`,
              editorType: "dxCheckBox",
              editorOptions: {
                text: getCustomerNominative(data.customer),
                readOnly: true
              },
              label: {
                visible: false
              }
            }
          ]
        : [];

    if (data.patient) {
      data.patient.relatives?.forEach((relative) => {
        initialFormData[`customer-${relative.id}`] = true;
      });
    } else {
      if (data.customer) {
        initialFormData[`customer-${data.customer.id}`] = true;

        if (data.customer.pets?.length === 1) {
          initialFormData["patientId"] = first(data.customer.pets)!.id;
          initialFormData["patientName"] = first(data.customer.pets)!.name;
        }
      }
    }

    initialFormData["type"] = cloneData?.type || data?.type || "unknown";
    initialFormData["channel"] = "email";

    let dxForm: DxFormComponent | undefined;
    let editor: Editor | undefined;

    if (cloneData) {
      initialFormData = {
        ...initialFormData,
        ...cloneData
      };
    }

    if (cloneData?.absoluteDate) {
      initialFormData.expireDate = CUSTOM_EXPIRE_DATE;
      initialFormData.expireDateCustom = dateUtil(
        cloneData.expireDate
      ).toISOString();
    }

    await providers.popupService.createEntity<PopupData>({
      title:
        modalConfig?.title ||
        (data.patient
          ? sntTranslate(marker("Nuovo promemoria per {patientName}"), {
              patientName: data.patient.name
            })
          : sntTranslate(marker("Nuovo promemoria"))),
      initialFormData,
      onFormReady: (onFormReadyEvent: { dxForm: DxFormComponent }) => {
        dxForm = onFormReadyEvent.dxForm;

        if (cloneData?.expireDate === CUSTOM_EXPIRE_DATE) {
          dxForm?.instance.itemOption("notificationDate", "editorOptions", {
            readOnly: false,
            displayExpr: "display",
            valueExpr: "value",
            dataSource: buildNotificationDateDataSource(
              config?.mode,
              dateUtil(cloneData.expireDateCustom).toISOString()
            )
          });

          dxForm?.instance.itemOption("expireDateCustom", {
            editorOptions: {
              readOnly: false
            },
            validationRules: [{ type: "required" }]
          });

          dxForm?.instance.itemOption("recurring", "editorOptions", {
            readOnly: true,
            switchedOnText: sntTranslate(marker("Si")),
            switchedOffText: sntTranslate(marker("No"))
          });
        } else {
          dxForm?.instance.itemOption("notificationDate", "editorOptions", {
            readOnly: false,
            displayExpr: "display",
            valueExpr: "value",
            dataSource: []
          });

          dxForm?.instance.itemOption("expireDateCustom", {
            editorOptions: {
              readOnly: true
            },
            validationRules: []
          });

          dxForm?.instance.itemOption("recurring", "editorOptions", {
            readOnly: false,
            switchedOnText: sntTranslate(marker("Si")),
            switchedOffText: sntTranslate(marker("No"))
          });
        }

        if (cloneData?.expireDate) {
          dxForm?.instance.itemOption("notificationDate", "editorOptions", {
            readOnly: false,
            displayExpr: "display",
            valueExpr: "value",
            dataSource: buildNotificationDateDataSource(
              config?.mode,
              dateUtil(cloneData.expireDate).toISOString()
            )
          });
        }
      },
      modalConfig: {
        width: 1148,
        height: 710,
        dialog: !!modalConfig?.dialog
      },
      confirmText: sntTranslate(marker("Salva")),
      confirmIcon: "fa-light fa-save",
      customActions: compact([
        !config?.disableWhatsApp
          ? {
              type: "outlined",
              text: sntTranslate(marker("Salva e invia su WhatsApp")),
              icon: "fa-brands fa-whatsapp",
              onClickAsync: async (
                data: PopupData & Record<string, any>,
                close: () => void,
                setDirtyFn: (dirty: boolean) => void
              ) => {
                const newReminder = await createReminderNoUI(
                  providers.bootstrapService.getCurrentMember()!,
                  providers.bootstrapService.getCurrentTenant()!,
                  data,
                  providers
                );
                setDirtyFn(false);

                if (newReminder) {
                  await reminderSendWhatsApp(
                    {
                      reminder: newReminder,
                      recipient: first(newReminder.recipients)!
                    },
                    {
                      customerBackend: providers.customerBackend,
                      reminderBackend: providers.reminedBackend,
                      toastService: providers.toastService,
                      bootstrapService: providers.bootstrapService,
                      documentService: providers.documentService,
                      patientBackend: providers.patientBackend
                    }
                  );
                }

                await close();

                res();
              }
            }
          : null,
        !config?.disableClone
          ? {
              type: "outlined",
              icon: "fa-light fa-clone",
              text: sntTranslate(marker("Salva e aggiungi nuovo")),
              onClickAsync: async (
                clonedData: PopupData & Record<string, any>,
                close: () => Promise<void>,
                setDirtyFn: (dirty: boolean) => void
              ) => {
                await createReminderNoUI(
                  providers.bootstrapService.getCurrentMember()!,
                  providers.bootstrapService.getCurrentTenant()!,
                  clonedData,
                  providers
                );
                setDirtyFn(false);

                setTimeout(async () => {
                  await close();
                  createReminderFunction(
                    {
                      patient: data.patient,
                      customer: data.customer
                    },
                    providers,
                    clonedData
                  )
                    .then(() => {
                      res();
                    })
                    .catch(() => {
                      res();
                    });
                }, 500);
              }
            }
          : null
      ]),
      formItems: compact([
        config?.mode === "template"
          ? null
          : {
              itemType: "group",
              colCount: 4,
              items: [
                getDxFormItemPatientId(
                  () => data.patient?.owner?.id || data.customer?.id,
                  {
                    applicationRef: providers.applicationRef,
                    customerBackend: providers.customerBackend,
                    patientBackend: providers.patientBackend
                  },
                  {
                    readOnly: !!data.patient
                  },
                  {
                    text: sntTranslate(marker("Paziente")),
                    visible: false
                  }
                ),
                {
                  itemType: "group",
                  colSpan: 3,
                  items: [
                    getDxCustomLabelFormItem(
                      sntTranslate(
                        customersFormItems.length === 1
                          ? marker("Destinatario")
                          : marker("Destinatari")
                      ),
                      "fa-light fa-users"
                    ),
                    {
                      itemType: "group",
                      colCount: 4,
                      items: customersFormItems
                    }
                  ]
                }
              ]
            },

        {
          itemType: "group",
          colCount: 13,
          items: compact([
            // 2
            {
              itemType: "group",
              colCount: 1,
              colSpan: 2,
              items: [
                getDxCustomLabelFormItem(
                  sntTranslate(marker("Canale")),
                  "fa-light fa-message-sms",
                  true
                ),
                {
                  dataField: "channel",
                  editorType: "dxSelectBox",
                  editorOptions: {
                    valueExpr: "value",
                    displayExpr: "display",
                    dataSource: [
                      {
                        value: "unknown",
                        display: sntTranslate(marker("Manuale")),
                        disabled: false
                      },
                      {
                        value: "email",
                        display: sntTranslate(marker("Email")),
                        disabled: false
                      },
                      {
                        value: "sms",
                        display: sntTranslate(marker("SMS")),
                        disabled: true
                      },
                      {
                        value: "whatsapp",
                        display: sntTranslate(marker("Whatsapp")),
                        disabled: true
                      }
                    ],
                    itemTemplate: (
                      {
                        value,
                        display
                      }: { value: ReminderTransport; display: string },
                      index: number,
                      element: HTMLElement
                    ) => {
                      let icon;

                      switch (value) {
                        case "email": {
                          icon = "fa-light fa-envelope";
                          break;
                        }
                        case "sms": {
                          icon = "fa-light fa-message-sms";
                          break;
                        }
                        case "unknown": {
                          icon = "fa-light fa-circle-question";
                          break;
                        }
                        case "whatsapp": {
                          icon = "fa-brands fa-whatsapp";
                          break;
                        }
                        case "sse": {
                          icon = "fa-light fa-signal-stream";
                          break;
                        }
                      }

                      element.innerHTML = `<div class="flex flex-row items-center">
            <div class="flex items-center justify-center w-6 h-6">
                <i class="${icon} text-slate-900"></i>
            </div>
            <span class="text-xs text-slate-900">${display}</span>
          </div>`;
                    },
                    onValueChanged: ({
                      value
                    }: {
                      value: "email" | "unknown";
                    }) => {
                      switch (value) {
                        case "email": {
                          dxForm?.instance.itemOption("subject", {
                            editorOptions: {
                              readOnly: false,
                              placeholder: ""
                            }
                          });
                          dxForm?.instance.updateData({
                            subject: data.patient
                              ? sntTranslate(marker("Promemoria per name"), {
                                  name: data.patient!.name
                                })
                              : ""
                          });
                          break;
                        }
                        case "unknown": {
                          dxForm?.instance.updateData({
                            subject: ""
                          });
                          dxForm?.instance.itemOption("subject", {
                            editorOptions: {
                              placeholder: sntTranslate(
                                marker("Oggetto del promemoria")
                              )
                            }
                          });
                          break;
                        }
                      }
                    }
                  },
                  validationRules: [{ type: "required" }],
                  label: { visible: false }
                }
              ]
            },

            // 2
            config?.mode !== "template"
              ? {
                  itemType: "group",
                  colCount: 1,
                  colSpan: 2,
                  items: [
                    getDxCustomLabelFormItem(
                      sntTranslate(marker("Inserito il")),
                      "fa-light fa-calendar-clock",
                      true
                    ),
                    {
                      dataField: "postingDate",
                      editorType: "dxTextBox",
                      editorOptions: {
                        readOnly: true
                      },
                      label: {
                        visible: false
                      }
                    }
                  ]
                }
              : null,

            // 3
            {
              itemType: "group",
              colCount: 1,
              colSpan: 3,
              items: [
                getDxCustomLabelFormItem(
                  sntTranslate(marker("Scadenza")),
                  "fa-light fa-calendar-clock",
                  true
                ),
                {
                  dataField: "expireDate",
                  editorType: "dxSelectBox",
                  editorOptions: {
                    readOnly: false,
                    displayExpr: "display",
                    valueExpr: "value",
                    dataSource: buildExpireDateDataSource(config?.mode),
                    onValueChanged: (dxEvent: ValueChangedEvent) => {
                      switch (dxEvent.value) {
                        case CUSTOM_EXPIRE_DATE: {
                          dxForm?.instance.itemOption("expireDateCustom", {
                            editorOptions: {
                              readOnly: false
                            },
                            validationRules: [{ type: "required" }]
                          });

                          dxForm?.instance.itemOption(
                            "notificationDate",
                            "editorOptions",
                            {
                              readOnly: false,
                              displayExpr: "display",
                              valueExpr: "value",
                              dataSource: []
                            }
                          );

                          dxForm?.instance.itemOption(
                            "recurring",
                            "editorOptions",
                            {
                              readOnly: true,
                              switchedOnText: sntTranslate(marker("Si")),
                              switchedOffText: sntTranslate(marker("No"))
                            }
                          );

                          dxForm?.instance.updateData({
                            recurring: false
                          });

                          break;
                        }
                        default: {
                          const expireDate = dateUtil(
                            dxEvent.value
                          ).toISOString();

                          dxForm?.instance.updateData({
                            notificationDate: undefined,
                            expireDateCustom: undefined
                          });

                          dxForm?.instance.itemOption(
                            "notificationDate",
                            "editorOptions",
                            {
                              readOnly: false,
                              displayExpr: "display",
                              valueExpr: "value",
                              dataSource: buildNotificationDateDataSource(
                                config?.mode,
                                expireDate
                              )
                            }
                          );

                          dxForm?.instance.itemOption("expireDateCustom", {
                            editorOptions: {
                              readOnly: true
                            },
                            validationRules: []
                          });

                          dxForm?.instance.itemOption(
                            "recurring",
                            "editorOptions",
                            {
                              readOnly: false,
                              switchedOnText: sntTranslate(marker("Si")),
                              switchedOffText: sntTranslate(marker("No"))
                            }
                          );
                          break;
                        }
                      }
                    }
                  },
                  label: {
                    visible: false
                  },
                  validationRules: [{ type: "required" }]
                }
              ]
            },

            // 2
            config?.mode !== "template"
              ? {
                  itemType: "group",
                  colSpan: 2,
                  colCount: 1,
                  items: [
                    getDxCustomLabelFormItem(
                      sntTranslate(marker("Data di scadenza")),
                      "fa-light fa-calendar-clock",
                      false
                    ),
                    {
                      colSpan: 2,
                      dataField: "expireDateCustom",
                      editorType: "dxDateBox",
                      editorOptions: {
                        readOnly: true,
                        onValueChanged: (dxEvent: ValueChangedEvent) => {
                          const expireDate = dateUtil(
                            dxEvent.value
                          ).toISOString();

                          dxForm?.instance.itemOption(
                            "notificationDate",
                            "editorOptions",
                            {
                              readOnly: false,
                              displayExpr: "display",
                              valueExpr: "value",
                              dataSource: buildNotificationDateDataSource(
                                config?.mode,
                                expireDate
                              )
                            }
                          );
                        }
                      },
                      label: {
                        visible: false
                      },
                      validationRules: []
                    }
                  ]
                }
              : null,

            // 3
            {
              itemType: "group",
              colCount: 1,
              colSpan: 3,
              items: [
                getDxCustomLabelFormItem(
                  sntTranslate(marker("Notifica")),
                  "fa-light fa-bell",
                  true
                ),
                {
                  dataField: "notificationDate",
                  editorType: "dxSelectBox",
                  editorOptions: {
                    readOnly: !initialFormData.expireDate,
                    displayExpr: "display",
                    valueExpr: "value",
                    dataSource: buildNotificationDateDataSource(
                      config?.mode,
                      initialFormData?.expireDate
                    )
                  },
                  label: {
                    visible: false
                  },
                  validationRules: [{ type: "required" }]
                }
              ]
            },

            // 1
            {
              itemType: "group",
              colCount: 1,
              colSpan: 1,
              items: [
                getDxCustomLabelFormItem(
                  sntTranslate(marker("Ricorrente")),
                  "fa-light fa-repeat",
                  false
                ),
                {
                  dataField: "recurring",
                  editorType: "dxSwitch",
                  editorOptions: {
                    readOnly: false,
                    switchedOnText: sntTranslate(marker("Si")),
                    switchedOffText: sntTranslate(marker("No"))
                  },
                  label: {
                    visible: false
                  }
                }
              ]
            }
          ])
        },

        getDxCustomLabelFormItem(
          sntTranslate(marker("Oggetto")),
          "fa-light fa-message-text",
          true
        ),
        {
          dataField: "subject",
          editorType: "dxTextBox",
          editorOptions: {
            placeholder: sntTranslate(marker("Oggetto del promemoria"))
          },
          label: { visible: false },
          validationRules: [{ type: "required" }]
        },

        {
          itemType: "group",
          colCount: 3,
          items: [
            {
              itemType: "group",
              colCount: 1,
              items: [
                getDxCustomLabelFormItem(
                  sntTranslate(marker("Modello")),
                  "fa-light fa-message-text",
                  true
                ),
                {
                  dataField: "templateId",
                  editorType: "dxSelectBox",
                  editorOptions: {
                    showClearButton: true,
                    dataSource: new DataSource({
                      paginate: false,
                      store: new CustomStore({
                        byKey: async (id: string) => {
                          return await lastValueFrom(
                            providers.reminderTemplateBackend.load(id).pipe(
                              map((reminder) => ({
                                ...reminder,
                                value: reminder.id,
                                display: reminder.name
                              }))
                            )
                          );
                        },
                        load: async () => {
                          return await lastValueFrom(
                            providers.reminderTemplateBackend
                              .listAllByType(MessageTemplateType.UNKNOWN)
                              .pipe(
                                map((templates) =>
                                  templates.map((x) => ({
                                    ...x,
                                    value: x.id,
                                    display: x.name
                                  }))
                                )
                              )
                          );
                        }
                      })
                    }),
                    displayExpr: "display",
                    valueExpr: "value",
                    onValueChanged: (dxEvent: ValueChangedEvent) => {
                      if (!dxEvent.value) {
                        editor?.setContent("");
                      }

                      dxFormRunAsyncOperation(dxForm!, async () => {
                        const templateId = dxEvent.value;

                        const template = await lastValueFrom(
                          providers.reminderTemplateBackend.load(templateId)
                        );

                        let htmlContent: string | undefined;
                        let computedProperties: Record<string, string> = {};

                        switch (config?.mode) {
                          case "template": {
                            htmlContent = template.content;
                            break;
                          }
                          default: {
                            let customerId = data.customer?.id;

                            if (!customerId) {
                              customerId = data.patient?.owner?.id;
                            }

                            if (
                              !customerId &&
                              data.patient?.relatives &&
                              data.patient?.relatives[0]
                            ) {
                              customerId = data.patient?.relatives[0]?.id;
                            }

                            const [customer, patient] = await Promise.all([
                              customerId
                                ? lastValueFrom(
                                    providers.customerBackend.load(customerId)
                                  )
                                : Promise.resolve(undefined),
                              dxForm?.formData.patientId
                                ? lastValueFrom(
                                    providers.patientBackend.load({
                                      id: dxForm.formData.patientId
                                    })
                                  )
                                : Promise.resolve(undefined)
                            ]);

                            const renderResult =
                              await providers.documentService.renderWithComputedSupportToHtml(
                                template.content,
                                async () => {
                                  return <DocumentDataModel>{
                                    customer: customer,
                                    patient: patient,
                                    tenant:
                                      providers.bootstrapService.getCurrentTenant(),
                                    reminder: {
                                      name: template.name,
                                      expiresAt: dxForm?.formData.expireDate,
                                      scheduledAt:
                                        dxForm?.formData.notificationDate
                                    }
                                  };
                                }
                              );

                            htmlContent = renderResult.rendered;

                            toPairs(renderResult.computedProperties).forEach(
                              ([key, value]) => {
                                computedProperties[key.replaceAll(".", "$")] =
                                  value;
                              }
                            );

                            break;
                          }
                        }

                        dxForm?.instance.updateData({
                          body: htmlContent,
                          computedProperties: computedProperties
                        });
                        editor?.setContent(htmlContent);
                      });
                    }
                  },
                  label: {
                    visible: false
                  },
                  validationRules: [{ type: "required" }]
                }
              ]
            }
          ]
        },

        getDxCustomLabelFormItem(
          sntTranslate(marker("Testo")),
          "fa-light fa-message-text",
          false,
          undefined,
          sntTranslate(
            marker(
              "Le sezioni delimitate da {{...}} verranno sostituite automaticamente con il valore corretto prima dell'effettivo invio."
            )
          )
        ),
        {
          dataField: "body",
          editorOptions: {},
          label: { visible: false },
          validationRules: [{ type: "required" }],
          template: async (item: any, element: HTMLElement) => {
            editor = await initWysiwygEditor(
              element,
              (content: string) => {
                dxForm?.instance.updateData({
                  body: content
                });
              },
              {
                toolbar:
                  "undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist checklist | forecolor backcolor casechange permanentpen formatpainter removeformat | pagebreak | charmap emoticons | fullscreen  preview",
                height: 370
              },
              initialFormData.body
            );
          }
        }
      ]),
      create: async (data: PopupData & Record<string, any>) => {
        await createReminderNoUI(
          providers.bootstrapService.getCurrentMember()!,
          providers.bootstrapService.getCurrentTenant()!,
          data,
          providers,
          overrides,
          data.computedProperties
        );
        res();
      },
      onCancel: async () => {
        res();
      }
    });
  });
}

export async function createReminderNoUI(
  member: Member,
  tenant: Tenant,
  data: PopupData & Record<string, any>,
  providers: CreateReminderFunctionProviders,
  overrides?: {
    silently?: boolean;
    reminderCreateReady?: (
      reminderCreate: ReminderCreate & ReminderTemplate
    ) => void;
  },
  computedProperties?: Record<string, string>
): Promise<Reminder | undefined> {
  if (!data.templateId && !data.subject) {
    throw new Error(sntTranslate(marker("Oggetto necessario")));
  }

  if (!data.templateId && !data.body) {
    throw new Error(sntTranslate(marker("Testo necessario")));
  }

  const customerIds = keys(data)
    .filter((x) => x.startsWith("customer-") && !!data[x])
    .map((key) => key.split("-")[1]);

  const recipients = await Promise.all(
    customerIds.map((id) =>
      lastValueFrom(
        providers.customerBackend.load(id).pipe(
          map((customer) => ({
            id: customer.id,
            name: getCustomerNominative(customer),
            email: customer.email
            //phone: customer.phone
          }))
        )
      )
    )
  );

  const payload: ReminderCreate & ReminderTemplate = {
    type: data.type,
    expiresAt:
      data.expireDate === CUSTOM_EXPIRE_DATE
        ? dateUtil(data.expireDateCustom).toISOString()
        : dateUtil(data.expireDate).toISOString(),
    scheduledAt: dateUtil(data.notificationDate).toISOString(),
    transport: data.channel,
    recipients: recipients,
    templateId: environment.snoots.templates.reminder_messages,
    subject: data.subject,
    body: data.body,
    sender: {
      id: member.id,
      name: tenant.name
    },
    absoluteDate: data.expireDate === CUSTOM_EXPIRE_DATE,
    data: {
      clinicLogo: <string>getPublicFileSrcById(tenant?.logo, {
        useFallback: true
      }),
      clinicAddress: tenant.address?.address || "",
      clinicCity: tenant.address?.city || "",
      clinicName: tenant.name,
      clinicPhone: tenant.phone || "",
      clinicState: tenant.address?.state || "",
      clinicZipCode: tenant.address?.zipCode || "",
      computedProperties: computedProperties
    },
    resources: data.resources || []
  };

  if (data.recurring) {
    payload.frequency =
      Math.abs(dateUtil().diff(dateUtil(data.expireDate), "months")) + 1;
  }

  if (overrides?.reminderCreateReady) {
    overrides.reminderCreateReady(payload);
    return undefined;
  }

  if (!payload.resources) {
    payload.resources = new Array<string>();
  }

  const keysResources = ["patientId"];

  keysResources.forEach((key) => {
    if (data[key]) {
      payload.resources.push(data[key]);
    }
  });

  if (customerIds?.length > 0) {
    customerIds.forEach((customerId) => payload.resources.push(customerId));
  }

  return overrides?.silently
    ? await lastValueFrom(providers.reminedBackend.createSilently(payload))
    : await lastValueFrom(providers.reminedBackend.create(payload));
}

function buildExpireDateDataSource(mode?: "template"): DataSource {
  const months = [1, 2, 3, 4, 5, 6, 12, 24, 36];

  let data: Array<{ value: string | number; display: string }>;

  switch (mode) {
    case "template": {
      data = months.map((x) => {
        return {
          value: dateUtil()
            .add(x, "months")
            .startOf("hour")
            .set("hours", 12)
            .toISOString(),
          display: sntTranslate(marker("in months"), {
            months: x.toString()
          })
        };
      });
      break;
    }
    default: {
      data = [
        {
          value: CUSTOM_EXPIRE_DATE,
          display: sntTranslate(marker("Personalizzata"))
        },
        ...months.map((x) => {
          return {
            value: dateUtil()
              .add(x, "months")
              .startOf("hour")
              .set("hours", 12)
              .toISOString(),
            display: `${dayjs
              .duration(
                -1 * dateUtil().diff(dateUtil().add(x, "months"), "seconds"),
                "seconds"
              )
              .humanize(true)}, il ${dateUtil()
              .add(x, "months")
              .format("DD/MM/YYYY")}`
          };
        })
      ];
      break;
    }
  }

  return new DataSource({
    store: new ArrayStore({
      data
    })
  });
}

function buildNotificationDateDataSource(
  mode?: "template",
  expireDate?: string
): DataSource {
  const days = [7, 14, 21, 30];

  let data: Array<{ value: string | number; display: string }>;

  switch (mode) {
    case "template": {
      data = days.map((days) => {
        return {
          value: dateUtil(expireDate)
            .subtract(days, "days")
            .startOf("hour")
            .set("hours", 12)
            .toISOString(),
          display: sntTranslate(marker("days prima"), {
            days
          })
        };
      });
      break;
    }
    default: {
      data = expireDate
        ? days.map((days) => {
            return {
              display: `${dayjs
                .duration(days, "days")
                .humanize(false)} prima, il ${dateUtil(expireDate)
                .subtract(days, "days")
                .format("DD/MM/YYYY")}`,
              value: dateUtil(expireDate)
                .subtract(days, "days")
                .startOf("hour")
                .set("hours", 12)
                .toISOString()
            };
          })
        : [];
      break;
    }
  }

  return new DataSource({
    store: new ArrayStore({
      data
    })
  });
}
